// src/components/bot/questions/InteractiveListQuestion.tsx
// 🎨 INTERACTIVE LIST QUESTION - Enhanced UX with Real WhatsApp Preview
// ✅ UPDATED: Removed false claims about searchable lists and user-friendly buttons

import React, { useState } from 'react';
import BaseQuestion from './BaseQuestion';
import styles from './Questions.module.css';
import { QuestionItem } from '../hooks/useBotForm';
import logger from '../../../utils/logger';

interface InteractiveListQuestionProps {
  question: QuestionItem;
  index: number;
  onUpdate: (id: string, field: keyof QuestionItem, value: any) => void;
  onRemove: (id: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  forceCollapsed?: boolean;
}

const InteractiveListQuestion: React.FC<InteractiveListQuestionProps> = (props) => {
  const { question, onUpdate } = props;
  
  logger.log('📋 InteractiveListQuestion: Rendering question:', question.id, question.interactive);
  
  // 🔄 SYNC UPDATE HANDLER: Keep question.question and interactive.body.text in sync
  const handleUpdate = (id: string, field: keyof QuestionItem, value: any) => {
    if (field === 'question') {
      // When question text is updated, also update interactive body text
      const updatedInteractive = {
        ...question.interactive,
        body: { text: value }
      };
      onUpdate(id, 'interactive', updatedInteractive);
    }
    // Always call the original update
    onUpdate(id, field, value);
  };
  
  // 🎨 EDIT STATES: Better UX with inline editing
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [isEditingButton, setIsEditingButton] = useState(false);
  const [isPreviewingList, setIsPreviewingList] = useState(false);
  
  // 🔥 INTERACTIVE DATA: Default structure with proper initialization
  const interactive = question.interactive || {
    type: 'list' as const,
    header: { type: 'text' as const, text: 'Select an option' },
    body: { text: question.question || 'Please choose from the list below' },
    action: { 
      button: 'View Options',
      sections: [
        {
          title: 'Options',
          rows: [
            { id: `row_${Date.now()}_1`, title: 'Option 1', description: 'First option' },
            { id: `row_${Date.now()}_2`, title: 'Option 2', description: 'Second option' }
          ]
        }
      ]
    }
  };

  // 🔥 UPDATE INTERACTIVE: Helper function to update interactive data
  const updateInteractive = (field: string, value: any) => {
    logger.log('🔄 InteractiveListQuestion: Updating interactive field:', field, value);
    const updatedInteractive = { ...interactive, [field]: value };
    onUpdate(question.id, 'interactive', updatedInteractive);
  };

  // 🔥 UPDATE ACTION: Helper function to update action data
  const updateAction = (field: string, value: any) => {
    logger.log('🔄 InteractiveListQuestion: Updating action field:', field, value);
    const updatedAction = { ...interactive.action, [field]: value };
    updateInteractive('action', updatedAction);
  };

  // 🔥 HEADER MANAGEMENT
  const handleHeaderSave = () => {
    setIsEditingHeader(false);
  };

  // 🔥 BUTTON MANAGEMENT
  const handleButtonSave = () => {
    setIsEditingButton(false);
  };

  // 🔥 SECTION MANAGEMENT: Add new section
  const addSection = () => {
    logger.log('➕ InteractiveListQuestion: Adding new section');
    const currentSections = interactive.action?.sections || [];
    const newSection = {
      title: `Section ${currentSections.length + 1}`,
      rows: [
        { id: `row_${Date.now()}_1`, title: 'New Option', description: 'Option description' }
      ]
    };
    updateAction('sections', [...currentSections, newSection]);
  };

  // 🔥 REMOVE SECTION: Delete section
  const removeSection = (sectionIndex: number) => {
    logger.log('🗑️ InteractiveListQuestion: Removing section:', sectionIndex);
    const currentSections = interactive.action?.sections || [];
    const updatedSections = currentSections.filter((_, index) => index !== sectionIndex);
    updateAction('sections', updatedSections);
  };

  // 🔥 UPDATE SECTION: Modify section data
  const updateSection = (sectionIndex: number, field: string, value: any) => {
    logger.log('📝 InteractiveListQuestion: Updating section:', sectionIndex, field, value);
    const updatedSections = [...(interactive.action?.sections || [])];
    updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], [field]: value };
    updateAction('sections', updatedSections);
  };

  // 🔥 ADD ROW: Add new row to section
  const addRow = (sectionIndex: number) => {
    logger.log('➕ InteractiveListQuestion: Adding new row to section:', sectionIndex);
    const updatedSections = [...(interactive.action?.sections || [])];
    const newRow = {
      id: `row_${Date.now()}_${Date.now()}`,
      title: 'New Option',
      description: 'Option description'
    };
    updatedSections[sectionIndex].rows.push(newRow);
    updateAction('sections', updatedSections);
  };

  // 🔥 REMOVE ROW: Delete row from section
  const removeRow = (sectionIndex: number, rowIndex: number) => {
    logger.log('🗑️ InteractiveListQuestion: Removing row:', sectionIndex, rowIndex);
    const updatedSections = [...(interactive.action?.sections || [])];
    updatedSections[sectionIndex].rows.splice(rowIndex, 1);
    updateAction('sections', updatedSections);
  };

  // 🔥 UPDATE ROW: Modify row data
  const updateRow = (sectionIndex: number, rowIndex: number, field: string, value: any) => {
    logger.log('📝 InteractiveListQuestion: Updating row:', sectionIndex, rowIndex, field, value);
    const updatedSections = [...(interactive.action?.sections || [])];
    updatedSections[sectionIndex].rows[rowIndex] = { 
      ...updatedSections[sectionIndex].rows[rowIndex], 
      [field]: value 
    };
    updateAction('sections', updatedSections);
  };

  // 🎨 GET STATS: For better UX feedback
  const getListStats = () => {
    const sections = interactive.action?.sections || [];
    const totalRows = sections.reduce((total, section) => total + section.rows.length, 0);
    return { sections: sections.length, totalRows };
  };

  const stats = getListStats();

  return (
    <BaseQuestion {...props} onUpdate={handleUpdate}>
      
      {/* 🎯 INTERACTIVE LIST OPTIONS */}
      <div className={styles.typeSpecificOptions}>
        
        {/* 🎨 ENHANCED WHATSAPP PREVIEW - Prominent placement */}
        <div className={styles.whatsappPreviewSection}>
          <div className={styles.previewHeader}>
            <span className={styles.previewLabel}>📱 WhatsApp Preview</span>
            <div className={styles.previewControls}>
              <button 
                type="button" 
                onClick={() => setIsPreviewingList(!isPreviewingList)}
                className={`${styles.previewToggle} ${isPreviewingList ? styles.previewToggleActive : ''}`}
              >
                {isPreviewingList ? (
                  <>
                    <span className={styles.toggleIcon}>📝</span>
                    <span>Hide List</span>
                  </>
                ) : (
                  <>
                    <span className={styles.toggleIcon}>👁️</span>
                    <span>Show List</span>
                  </>
                )}
              </button>
              <div className={styles.previewBadge}>
                📋 Interactive List
              </div>
            </div>
          </div>
          
          {/* 🎨 WHATSAPP STYLED PREVIEW */}
          <div className={styles.whatsappMockup}>
            <div className={styles.whatsappHeader}>
              <div className={styles.whatsappAvatar}>🤖</div>
              <div className={styles.whatsappContact}>Your Bot</div>
              <div className={styles.whatsappTime}>12:34 PM</div>
            </div>
            
            <div className={styles.whatsappMessage}>
              {/* Header */}
              {interactive.header?.text && (
                <div className={styles.whatsappMessageHeader}>
                  📋 {interactive.header.text}
                </div>
              )}
              
              {/* Body */}
              <div className={styles.whatsappMessageBody}>
                {interactive.body?.text || question.question || 'Please choose from the list below'}
                {question.required && <span className={styles.requiredIndicator}>*</span>}
              </div>
              
              {/* List Button - Clickable with instruction */}
              <div className={styles.listButtonContainer}>
                <div 
                  className={`${styles.whatsappListButton} ${isPreviewingList ? styles.listButtonActive : ''}`}
                  onClick={() => setIsPreviewingList(!isPreviewingList)}
                >
                  📋 {interactive.action?.button || 'View Options'} {isPreviewingList ? '▲' : '▼'}
                </div>
                <div className={styles.listButtonHint}>
                  👆 Click to {isPreviewingList ? 'close' : 'open'} list preview
                </div>
              </div>
              
              {/* List Preview (when expanded) - Enhanced Animation */}
              <div className={`${styles.whatsappListMenu} ${isPreviewingList ? styles.listMenuOpen : styles.listMenuClosed}`}>
                {(interactive.action?.sections || []).map((section, idx) => (
                  <div key={idx} className={styles.whatsappListSection}>
                    <div className={styles.whatsappSectionTitle}>
                      📂 {section.title}
                    </div>
                    {section.rows.map((row, rowIdx) => (
                      <div 
                        key={rowIdx} 
                        className={styles.whatsappListItem}
                        onClick={() => {
                          // Simulate item selection
                          const itemElement = document.querySelector(`[data-item="${section.title}-${row.title}"]`);
                          if (itemElement) {
                            itemElement.classList.add(styles.itemSelected);
                            setTimeout(() => {
                              itemElement.classList.remove(styles.itemSelected);
                              setIsPreviewingList(false);
                            }, 500);
                          }
                        }}
                        data-item={`${section.title}-${row.title}`}
                      >
                        <div className={styles.whatsappItemTitle}>✓ {row.title}</div>
                        {row.description && (
                          <div className={styles.whatsappItemDesc}>{row.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                
                {/* Empty State */}
                {(!interactive.action?.sections || interactive.action.sections.length === 0) && (
                  <div className={styles.whatsappListEmpty}>
                    <div className={styles.emptyIcon}>📋</div>
                    <div className={styles.emptyText}>No options configured yet</div>
                    <div className={styles.emptySubtext}>Add sections and options below</div>
                  </div>
                )}
              </div>
              
              <div className={styles.whatsappTimestamp}>12:34 PM ✓✓</div>
            </div>
          </div>
          
          {/* 🎨 STATS DISPLAY */}
          <div className={styles.listStats}>
            <span className={styles.statItem}>📂 {stats.sections} sections</span>
            <span className={styles.statItem}>📋 {stats.totalRows} options</span>
            <span className={styles.statItem}>
              {question.required ? '🔒 Required' : '📝 Optional'}
            </span>
          </div>
        </div>

        {/* 🔥 HEADER TEXT */}
        <div className={styles.optionRow}>
          <label className={styles.optionLabel}>Header text:</label>
          <div className={styles.inlineEditContainer}>
            {isEditingHeader ? (
              <div className={styles.inlineEditor}>
                <input 
                  type="text"
                  value={interactive.header?.text || ''}
                  onChange={(e) => updateInteractive('header', { type: 'text', text: e.target.value })}
                  placeholder="e.g., Select an option"
                  className={styles.inlineInput}
                  autoFocus
                  maxLength={60}
                />
                <div className={styles.inlineActions}>
                  <button type="button" onClick={handleHeaderSave} className={styles.saveBtn}>✓</button>
                  <button type="button" onClick={() => setIsEditingHeader(false)} className={styles.cancelBtn}>✕</button>
                </div>
              </div>
            ) : (
              <div className={styles.inlineDisplay} onClick={() => setIsEditingHeader(true)}>
                {interactive.header?.text || <span className={styles.placeholder}>Click to add header</span>}
                <span className={styles.editIcon}>✏️</span>
              </div>
            )}
          </div>
          <small className={styles.helpText}>
            Short header text shown at the top of the list
          </small>
        </div>

        {/* 🔥 BUTTON TEXT */}
        <div className={styles.optionRow}>
          <label className={styles.optionLabel}>Button text:</label>
          <div className={styles.inlineEditContainer}>
            {isEditingButton ? (
              <div className={styles.inlineEditor}>
                <input 
                  type="text"
                  value={interactive.action?.button || ''}
                  onChange={(e) => updateAction('button', e.target.value)}
                  placeholder="e.g., View Options"
                  className={styles.inlineInput}
                  autoFocus
                  maxLength={20}
                />
                <div className={styles.inlineActions}>
                  <button type="button" onClick={handleButtonSave} className={styles.saveBtn}>✓</button>
                  <button type="button" onClick={() => setIsEditingButton(false)} className={styles.cancelBtn}>✕</button>
                </div>
              </div>
            ) : (
              <div className={styles.inlineDisplay} onClick={() => setIsEditingButton(true)}>
                {interactive.action?.button || <span className={styles.placeholder}>Click to add button text</span>}
                <span className={styles.editIcon}>✏️</span>
              </div>
            )}
          </div>
          <small className={styles.helpText}>
            Text shown on the button to open the list
          </small>
        </div>

        {/* 🔥 SECTIONS MANAGEMENT */}
        <div className={styles.optionRow}>
          <label className={styles.optionLabel}>List sections and options:</label>
          <div className={styles.sectionsContainer}>
            
            {/* 🎨 SECTIONS EDITOR */}
            <div className={styles.sectionsEditor}>
              {(interactive.action?.sections || []).map((section, sectionIndex) => (
                <div key={sectionIndex} className={styles.sectionCard}>
                  
                  {/* Section Header */}
                  <div className={styles.sectionHeader}>
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => updateSection(sectionIndex, 'title', e.target.value)}
                      placeholder="Section title"
                      className={styles.sectionTitleInput}
                      maxLength={24}
                    />
                    <div className={styles.sectionActions}>
                      <span className={styles.charCount}>{section.title.length}/24</span>
                      <button 
                        type="button"
                        onClick={() => removeSection(sectionIndex)}
                        className={styles.removeSectionBtn}
                        title="Remove section"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  
                  {/* Section Rows */}
                  <div className={styles.sectionRows}>
                    {section.rows.map((row, rowIndex) => (
                      <div key={rowIndex} className={styles.rowCard}>
                        <div className={styles.rowInputs}>
                          <input
                            type="text"
                            value={row.title}
                            onChange={(e) => updateRow(sectionIndex, rowIndex, 'title', e.target.value)}
                            placeholder="Option title"
                            className={styles.rowTitleInput}
                            maxLength={24}
                          />
                          <input
                            type="text"
                            value={row.description || ''}
                            onChange={(e) => updateRow(sectionIndex, rowIndex, 'description', e.target.value)}
                            placeholder="Optional description"
                            className={styles.rowDescInput}
                            maxLength={72}
                          />
                        </div>
                        <button 
                          type="button"
                          onClick={() => removeRow(sectionIndex, rowIndex)}
                          className={styles.removeRowBtn}
                          title="Remove option"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    
                    {/* Add Row Button */}
                    <button
                      type="button"
                      onClick={() => addRow(sectionIndex)}
                      className={styles.addRowBtn}
                      disabled={section.rows.length >= 10}
                    >
                      ➕ Add Option ({section.rows.length}/10)
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Add Section Button */}
              <button
                type="button"
                onClick={addSection}
                className={styles.addSectionBtn}
                disabled={(interactive.action?.sections || []).length >= 10}
              >
                📂 Add Section ({(interactive.action?.sections || []).length}/10)
              </button>
            </div>
            
          </div>
          <small className={styles.helpText}>
            Organize your options into sections. Each section can have multiple rows.
          </small>
        </div>
        
      </div>

      {/* 🎯 WHATSAPP INFO - ✅ UPDATED: Removed false claims */}
      <div className={styles.whatsappInfo}>
        <div className={styles.infoHeader}>
          <i className="fab fa-whatsapp" style={{ color: '#25d366' }}></i>
          <span>WhatsApp List Features</span>
        </div>
        <div className={styles.infoContent}>
          <div className={styles.infoItem}>
            <i className="fas fa-check" style={{ color: '#10b981' }}></i>
            <span>Up to 10 sections with 10 rows each</span>
          </div>
          <div className={styles.infoItem}>
            <i className="fas fa-check" style={{ color: '#10b981' }}></i>
            <span>Organized with sections and descriptions</span>
          </div>
        </div>
      </div>
      
    </BaseQuestion>
  );
};

export default InteractiveListQuestion;